import {
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole
} from '@floating-ui/react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { ReactNode, useRef, useState } from 'react';

type InfoTooltipProps = {
  children?: ReactNode;
  constrainWidth?: boolean;
  // Names the icon for assistive tech and puts it in the tab order, so the
  // explanation is reachable without a mouse.
  ariaLabel?: string;
  iconClassName?: string;
};

export default function InfoTooltip({
  children,
  constrainWidth = true,
  ariaLabel,
  iconClassName = 'h-5 w-5 cursor-help text-gray-500 hover:text-gray-700'
}: InfoTooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        fallbackAxisSideDirection: 'start'
      }),
      shift(),
      arrow({
        element: arrowRef
      })
    ]
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role
  ]);

  // whitespace-pre-line: admin-entered tooltip content is plain text with
  // line breaks (e.g. one chassis size per line) — honor them when rendering.
  const classes = clsx(
    'w-max whitespace-pre-line bg-[#444] text-white text-sm px-2 py-1 rounded z-50',
    constrainWidth && 'max-w-[300px]'
  );

  return (
    <>
      <div
        className='inline-block'
        ref={refs.setReference}
        {...(ariaLabel && {
          tabIndex: 0,
          role: 'img',
          'aria-label': ariaLabel
        })}
        {...getReferenceProps()}
      >
        <InformationCircleIcon className={iconClassName} />
      </div>
      <FloatingPortal>
        {isOpen && (
          <div
            className={classes}
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <FloatingArrow fill='#444' ref={arrowRef} context={context} />
            {children}
          </div>
        )}
      </FloatingPortal>
    </>
  );
}
